import environment from '#/environment/environment';
import {Web3AuthSignatureGroup} from '#/hooks/use-web3auth-signature-group';
import {ApiRoute, ApiRouteData} from '@shareland/shareland-common';
import Cookies from 'js-cookie';

type Params<T extends ApiRoute> = Pick<T, 'paramsDictionary' | 'reqBody' | 'reqQuery'>;

const getEndpointPath = <T extends ApiRoute>(
  paramsDictionary: T['paramsDictionary'],
  clientEndpoint: T['clientEndpoint'],
): string => {
  if (!paramsDictionary) {
    return clientEndpoint;
  }

  return Object.entries(paramsDictionary).reduce((acc, [key, value]) => {
    //@ts-ignore
    return acc.replace(`:${key}`, value);
  }, clientEndpoint);
};

export const fetchFromApiRoute = async <T extends ApiRoute>(
  apiRouteData: ApiRouteData,
  params: Params<T>,
  throwOnError: boolean = false,
): Promise<T['resBody']['data']> => {
  const {method: methodLowercase, clientEndpoint} = apiRouteData;
  const {paramsDictionary, reqBody, reqQuery} = params;

  const method = methodLowercase.toUpperCase();

  const basePath = environment.apiUrl;
  const endpointPath = getEndpointPath(paramsDictionary, clientEndpoint);
  //@ts-ignore
  const searchParamsString = reqQuery ? `?${new URLSearchParams(reqQuery).toString()}` : '';

  const url = `${basePath}${endpointPath}${searchParamsString}`;

  const response = await fetch(url, {
    method,
    ...(method !== 'GET' && {
      headers: {
        'Content-Type': 'application/json',
      },
    }),
    ...(reqBody && {
      body: JSON.stringify(reqBody),
    }),
  });

  const json = await response.json();

  if (throwOnError && json.error) {
    throw new Error(json.error);
  }

  const data: T['resBody']['data'] = json.data;
  return data;
};

export type ApiBodyWithoutSignature<T> = Omit<T, 'publicAddress' | 'appPubKey'>;

export const fetchFromApiRouteWithSignature = async <T extends ApiRoute>(
  apiRouteData: ApiRouteData,
  params: {
    paramsDictionary: T['paramsDictionary'];
    reqBody: ApiBodyWithoutSignature<T['reqBody']>;
    reqQuery: T['reqQuery'];
  },
  web3AuthSignatureGroup: Web3AuthSignatureGroup,
  throwOnError: boolean = false,
): Promise<T['resBody']['data']> => {
  const {method: methodLowercase, clientEndpoint} = apiRouteData;
  const {paramsDictionary, reqBody, reqQuery} = params;

  const method = methodLowercase.toUpperCase();

  const basePath = environment.apiUrl;
  const endpointPath = getEndpointPath(paramsDictionary, clientEndpoint);
  //@ts-ignore
  const searchParamsString = reqQuery ? `?${new URLSearchParams(reqQuery).toString()}` : '';

  const url = `${basePath}${endpointPath}${searchParamsString}`;

  const response = await fetch(url, {
    method,
    ...(method !== 'GET' && {
      headers: {
        'Content-Type': 'application/json',
        ...(web3AuthSignatureGroup?.idToken && {Authorization: `Bearer ${web3AuthSignatureGroup.idToken}`}),
        'referrer': Cookies.get('referrer') || '',
      },
    }),
    ...(reqBody && {
      body: JSON.stringify({
        ...reqBody,
        ...(web3AuthSignatureGroup?.publicAddress && {publicAddress: web3AuthSignatureGroup.publicAddress}),
        ...(web3AuthSignatureGroup?.appPubKey && {appPubKey: web3AuthSignatureGroup.appPubKey}),
      }),
    }),
  });

  const json = await response.json();

  if (throwOnError && json.error) {
    throw new Error(json.error);
  }

  const data: T['resBody']['data'] = json.data;
  return data;
};
